import {
  BlocklistPolicy,
  describeDuration,
  roleAtLeast,
  type BlocklistEntry,
  type BlocklistStatus,
  type BlocklistSummary,
} from '@bartizo/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import {
  getBlocklistSummary,
  listBlocklist,
  putBlocklistPolicy,
  removeFromBlocklist,
} from '../api/blocklist.js';
import { useCurrentUser } from '../auth/session-context.js';
import { BlocklistRowDetail } from '../components/BlocklistRowDetail.js';
import { CauseBadges, CauseList } from '../components/BlocklistCauses.js';
import { BlocklistAddPanel } from '../components/BlocklistAddPanel.js';
import { EmptyState } from '../components/EmptyState.js';
import { BlocklistStatusBadge } from '../components/StatusBadge.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable, Facts, type Column } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { TextField } from '../ui/Field.js';
import { BanIcon, ClockIcon, FilterIcon, ServerIcon } from '../ui/icons.js';
import { Loading } from '../ui/Skeleton.js';
import { StatIcon } from '../ui/StatIcon.js';

/**
 * The fleet blocklist (knowledge doc section 12, "Fleet blocklist"; P11.6).
 *
 * What every server keeps out, beyond its own CrowdSec's bans: addresses that
 * earned it by the policy shown at the top, or that a person added. Each row says
 * why, how far up the ladder and until when; it opens into the evidence and what
 * can be done — take it off every server, or look at the address itself.
 */

const STATUS_FILTERS: readonly { value: BlocklistStatus | 'all'; label: string }[] = [
  { value: 'active', label: 'Blocked' },
  { value: 'protected', label: 'Protected' },
  { value: 'expired', label: 'Ended' },
  { value: 'removed', label: 'Removed' },
  { value: 'all', label: 'All' },
];

const errorText = (caught: unknown, fallback: string): string =>
  caught instanceof Error ? caught.message : fallback;

const ladderText = (ladder: readonly number[]): string =>
  ladder.map((seconds) => describeDuration(seconds)).join(' → ');

export function BlocklistPage(): ReactNode {
  const session = useCurrentUser();
  const canAct = roleAtLeast(session.user.role, 'operator');
  const isAdmin = roleAtLeast(session.user.role, 'admin');
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<BlocklistStatus | 'all'>('active');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<BlocklistEntry | null>(null);
  const [panel, setPanel] = useState<'add' | 'policy' | null>(null);

  const summary = useQuery({ queryKey: ['blocklist-summary'], queryFn: getBlocklistSummary });
  const filters = {
    ...(status === 'all' ? {} : { status }),
    ...(search.trim() === '' ? {} : { q: search.trim() }),
  };
  const list = useInfiniteQuery({
    queryKey: ['blocklist', filters],
    queryFn: ({ pageParam }) =>
      listBlocklist({ ...filters, ...(pageParam === undefined ? {} : { cursor: pageParam }) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['blocklist'] });
    void queryClient.invalidateQueries({ queryKey: ['blocklist-summary'] });
  };

  const columns: Column<BlocklistEntry>[] = [
    {
      key: 'ip',
      header: 'Address',
      rowHeader: true,
      sortValue: (row) => row.ip,
      cell: (row) => <code>{row.ip}</code>,
    },
    { key: 'status', header: 'State', cell: (row) => <BlocklistStatusBadge status={row.status} /> },
    {
      key: 'why',
      header: 'What blocked it',
      cell: (row) => <CauseBadges entry={row} />,
    },
    {
      key: 'level',
      header: 'Step',
      wideOnly: true,
      align: 'end',
      sortValue: (row) => row.level,
      cell: (row) => row.level,
    },
    {
      key: 'until',
      header: 'Until',
      sortValue: (row) => Date.parse(row.until),
      cell: (row) =>
        row.status === 'active' ? formatRelative(row.until) : <span className="muted">—</span>,
    },
    {
      key: 'seen',
      header: 'Last seen',
      wideOnly: true,
      sortValue: (row) => Date.parse(row.lastSeen),
      cell: (row) => formatRelative(row.lastSeen),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Blocklist"
        description="Addresses every server keeps out, longer than one CrowdSec ban: those banned again and again, or on several servers, and those a person added. New servers get the list within the hour."
      />

      <SummaryTiles summary={summary.data} />

      <Card
        title="Addresses"
        {...(canAct || isAdmin
          ? {
              aside: (
                <span className="row">
                  {isAdmin && (
                    <Button
                      onClick={() => {
                        setPanel('policy');
                      }}
                    >
                      Policy
                    </Button>
                  )}
                  {canAct && (
                    <Button
                      variant="primary"
                      onClick={() => {
                        setPanel('add');
                      }}
                    >
                      Add address
                    </Button>
                  )}
                </span>
              ),
            }
          : {})}
      >
        <div className="toolbar">
          <div className="segmented" role="group" aria-label="Show">
            {STATUS_FILTERS.map((each) => (
              <button
                key={each.value}
                type="button"
                aria-pressed={status === each.value}
                onClick={() => {
                  setStatus(each.value);
                }}
              >
                {each.label}
              </button>
            ))}
          </div>
          <div className="push-right">
            <TextField
              label="Find an address"
              labelHidden
              placeholder="Find an address"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value.replace(/[^0-9a-fA-F.:]/g, ''));
              }}
            />
          </div>
        </div>

        {list.isPending && <Loading />}
        {list.isError && (
          <Alert tone="danger" title="Could not load the list">
            {errorText(list.error, 'Try again in a moment.')}
          </Alert>
        )}
        {list.data !== undefined && (
          <DataTable
            caption="Addresses on the fleet blocklist"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.ip}
            onSelect={setOpen}
            selectedKey={open?.ip}
            renderDetail={(entry) => <BlocklistRowDetail entry={entry} />}
            pagination={{
              hasMore: list.hasNextPage,
              loadingMore: list.isFetchingNextPage,
              onLoadMore: () => {
                void list.fetchNextPage();
              },
            }}
            empty={
              <EmptyState
                title={status === 'active' ? 'Nothing is blocked fleet-wide yet' : 'None here'}
              >
                {status === 'active'
                  ? 'An address joins once CrowdSec bans it on enough servers, or often enough on one — the policy above says how much. You can also add one yourself.'
                  : 'Try another filter.'}
              </EmptyState>
            }
          />
        )}
      </Card>

      {open !== null && (
        <EntryPanel
          entry={open}
          canAct={canAct}
          onChanged={(entry) => {
            setOpen(entry);
            refresh();
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {panel === 'add' && (
        <BlocklistAddPanel
          ladder={summary.data?.policy.ladder_seconds ?? []}
          onDone={() => {
            setPanel(null);
            refresh();
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {panel === 'policy' && summary.data !== undefined && (
        <PolicyPanel
          summary={summary.data}
          onDone={() => {
            setPanel(null);
            refresh();
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
    </div>
  );
}

function SummaryTiles({ summary }: { summary: BlocklistSummary | undefined }): ReactNode {
  if (summary === undefined) return null;
  const { policy } = summary;
  const servers = summary.serversCurrent + summary.serversBehind;
  return (
    <dl className="stat-tiles">
      <div className="stat-tile">
        <StatIcon icon={BanIcon} tone="ok" />
        <dt>Blocked fleet-wide</dt>
        <dd>{summary.active.toLocaleString()}</dd>
        <p>At most {policy.max_entries.toLocaleString()} per server</p>
      </div>
      <div className={`stat-tile ${summary.serversBehind > 0 ? 'stat-tile-alert' : ''}`}>
        <StatIcon icon={ServerIcon} tone={summary.serversBehind > 0 ? 'danger' : 'ok'} />
        <dt>Servers up to date</dt>
        <dd>
          {summary.serversCurrent}
          <span className="stat-of"> of {servers}</span>
        </dd>
        <p>
          {summary.serversBehind === 0
            ? 'Every server holds the latest list'
            : `${String(summary.serversBehind)} behind or failing — they catch up within the hour`}
        </p>
      </div>
      <div className={`stat-tile ${policy.enabled ? '' : 'stat-tile-alert'}`}>
        <StatIcon icon={FilterIcon} tone={policy.enabled ? 'accent' : 'danger'} />
        <dt>Joins when banned</dt>
        <dd className="stat-text">
          {policy.enabled
            ? `${String(policy.min_servers)}+ servers or ${String(policy.min_bans)}+ times`
            : 'Off'}
        </dd>
        <p>Within {policy.window_days} days</p>
      </div>
      <div className="stat-tile">
        <StatIcon icon={ClockIcon} />
        <dt>Ban, each time it returns</dt>
        <dd className="stat-text">{ladderText(policy.ladder_seconds)}</dd>
        <p>Forgotten after {policy.quiet_days} quiet days</p>
      </div>
    </dl>
  );
}

function EntryPanel({
  entry,
  canAct,
  onChanged,
  onClose,
}: {
  entry: BlocklistEntry;
  canAct: boolean;
  onChanged: (entry: BlocklistEntry) => void;
  onClose: () => void;
}): ReactNode {
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');
  const remove = useMutation({
    mutationFn: () => removeFromBlocklist(entry.ip, reason.trim()),
    onSuccess: (next) => {
      setRemoving(false);
      onChanged(next);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (reason.trim() !== '') remove.mutate();
  };

  return (
    <Dialog
      variant="panel"
      eyebrow="Fleet blocklist"
      title={entry.ip}
      description={
        entry.status === 'active'
          ? `Kept out of every server until ${new Date(entry.until).toLocaleString()}.`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <Link
            className="button button-secondary"
            to={`/addresses/${encodeURIComponent(entry.ip)}`}
          >
            Open the address
          </Link>
          {canAct && entry.status === 'active' && !removing && (
            <Button
              variant="danger"
              onClick={() => {
                setRemoving(true);
              }}
            >
              Remove from every server
            </Button>
          )}
        </>
      }
    >
      <div className="row">
        <BlocklistStatusBadge status={entry.status} />
        <span className="muted text-sm">Step {entry.level} of the ladder</span>
      </div>

      {entry.refused !== null && (
        <Alert tone="info" title="No server may ban this address">
          {entry.refused}
        </Alert>
      )}
      {entry.status === 'removed' && (
        <Alert tone="info" title={`Removed by ${entry.removedByEmail ?? 'a person'}`}>
          {entry.removedReason ?? 'No reason given.'} It stays off the list until it is banned again
          after that.
        </Alert>
      )}

      <section>
        <h3>What blocked it</h3>
        <CauseList entry={entry} />
        {entry.source === 'auto' && <p className="text-sm muted">{entry.reason}</p>}
      </section>

      <Facts
        items={[
          ['Banned', `${String(entry.bans)} time${entry.bans === 1 ? '' : 's'}`],
          ['On servers', String(entry.servers)],
          ['First seen', formatRelative(entry.firstSeen)],
          ['Last seen', formatRelative(entry.lastSeen)],
          ['Until', entry.status === 'active' ? formatRelative(entry.until) : '—'],
          [
            'Servers that skip it',
            entry.excludedServers === 0
              ? 'None'
              : `${String(entry.excludedServers)} (proxies or no CrowdSec)`,
          ],
          [
            'Added',
            entry.source === 'manual' ? `By ${entry.addedByEmail ?? 'a person'}` : 'By the policy',
          ],
        ]}
      />

      {removing && (
        <form onSubmit={submit} className="stack">
          <TextField
            label="Why it should come off"
            hint="Kept in the audit log. Every server drops it within the hour."
            autoFocus
            maxLength={300}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          {remove.isError && (
            <Alert tone="danger">{errorText(remove.error, 'Could not remove it.')}</Alert>
          )}
          <div className="row">
            <Button
              onClick={() => {
                setRemoving(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={reason.trim() === ''}
              busy={remove.isPending}
              busyLabel="Removing…"
            >
              Remove it
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** "4h, 1d, 7d" ↔ seconds. */
function parseLadder(text: string): number[] | undefined {
  const units: Record<string, number> = { m: 60, h: 3600, d: 86_400 };
  const steps = text
    .split(/[\s,→]+/)
    .filter((part) => part !== '')
    .map((part) => {
      const match = /^(\d+)([mhd])$/.exec(part);
      return match === null ? Number.NaN : Number(match[1]) * (units[match[2] ?? 'h'] ?? 0);
    });
  return steps.length === 0 || steps.some((step) => Number.isNaN(step)) ? undefined : steps;
}

function PolicyPanel({
  summary,
  onDone,
  onClose,
}: {
  summary: BlocklistSummary;
  onDone: () => void;
  onClose: () => void;
}): ReactNode {
  const { policy } = summary;
  const [enabled, setEnabled] = useState(policy.enabled);
  const [minServers, setMinServers] = useState(String(policy.min_servers));
  const [minBans, setMinBans] = useState(String(policy.min_bans));
  const [windowDays, setWindowDays] = useState(String(policy.window_days));
  const [quietDays, setQuietDays] = useState(String(policy.quiet_days));
  const [ladder, setLadder] = useState(
    policy.ladder_seconds.map((step) => describeDuration(step)).join(', '),
  );
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (next: BlocklistPolicy) => putBlocklistPolicy(next),
    onSuccess: onDone,
  });

  const submit = (): void => {
    const steps = parseLadder(ladder);
    const parsed = BlocklistPolicy.safeParse({
      ...policy,
      enabled,
      min_servers: Number(minServers),
      min_bans: Number(minBans),
      window_days: Number(windowDays),
      quiet_days: Number(quietDays),
      ladder_seconds: steps ?? [],
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setProblem(
        steps === undefined
          ? 'Write the ladder as durations such as 4h, 1d, 7d, 30d.'
          : `${issue?.path.join('.') ?? ''}: ${issue?.message ?? 'check the values'}`,
      );
      return;
    }
    setProblem(null);
    save.mutate(parsed.data);
  };

  return (
    <Dialog
      variant="panel"
      eyebrow="Fleet blocklist"
      title="Policy"
      description="When an address joins the list, how long it is kept out each time it returns, and when it is forgotten. Applies to the whole fleet."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={save.isPending} busyLabel="Saving…" onClick={submit}>
            Save policy
          </Button>
        </>
      }
    >
      <label className="checkbox">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => {
            setEnabled(event.target.checked);
          }}
        />
        Add addresses by this policy (people can always add their own)
      </label>
      <div className="form-grid">
        <TextField
          label="Banned on at least (servers)"
          type="number"
          min={1}
          max={50}
          value={minServers}
          onChange={(event) => {
            setMinServers(event.target.value);
          }}
        />
        <TextField
          label="Or on one server (times)"
          type="number"
          min={2}
          max={100}
          value={minBans}
          onChange={(event) => {
            setMinBans(event.target.value);
          }}
        />
        <TextField
          label="Within (days)"
          type="number"
          min={1}
          max={90}
          value={windowDays}
          onChange={(event) => {
            setWindowDays(event.target.value);
          }}
        />
        <TextField
          label="Forget after quiet (days)"
          hint="History and ended entries go then; IP addresses are personal data."
          type="number"
          min={7}
          max={365}
          value={quietDays}
          onChange={(event) => {
            setQuietDays(event.target.value);
          }}
        />
        <div className="span-all">
          <TextField
            label="Ban for, each time it returns"
            hint="Durations in order, such as 4h, 1d, 7d, 30d, 90d. The last step repeats."
            value={ladder}
            onChange={(event) => {
              setLadder(event.target.value);
            }}
          />
        </div>
      </div>
      {problem !== null && <Alert tone="danger">{problem}</Alert>}
      {save.isError && <Alert tone="danger">{errorText(save.error, 'Could not save it.')}</Alert>}
    </Dialog>
  );
}
